import { appendAttachments, appendCapturedContext, bugReportApi } from './bugReportApi';
import type { IBugReportCreated } from './bugReportApi';

/**
 * "Request a Feature" form in the global header
 * (`pages/dashboard/components/header/FeatureRequestPopup.tsx`).
 *
 * One endpoint: POST /api/v1.0/feature-requests/ (`django/api/rest_feature_request/`).
 * It is the Report a Bug pipeline with `kind: "feature_request"` on the message
 * (`django/bug_report/intake.py`), so everything in `bugReportApi.ts` about the
 * 202/503 answers, multipart bodies and attachment rules applies unchanged.
 *
 * Injected into `bugReportApi` rather than a second `createApi`: same base
 * query, same pipeline, and no extra reducer/middleware to register in the store.
 */

/** What the requester typed. Everything else is collected automatically. */
export interface IFeatureRequestDraft {
  subject: string;
  description: string;
  why: string;
  area: string;
  attachments?: File[];
}

/** No `severity` or `steps`: the pipeline's contract is that a feature request
 *  does not carry them at all. */
const toFormData = (draft: IFeatureRequestDraft): FormData => {
  const body = new FormData();
  body.append('subject', draft.subject.trim());
  body.append('description', draft.description.trim());
  body.append('why', draft.why.trim());
  body.append('area', draft.area);
  appendCapturedContext(body);
  appendAttachments(body, draft.attachments);
  return body;
};

export const featureRequestApi = bugReportApi.injectEndpoints({
  endpoints: (builder) => ({
    createFeatureRequest: builder.mutation<IBugReportCreated, IFeatureRequestDraft>({
      query: (draft) => ({
        url: '/api/v1.0/feature-requests/',
        method: 'POST',
        body: toFormData(draft),
      }),
    }),
  }),
});

export const { useCreateFeatureRequestMutation } = featureRequestApi;
